import { Box, Typography, Button } from "@mui/material";
import { Add } from "@mui/icons-material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import React, { useEffect, useState, useRef } from "react";
import { defaultParams } from "../../App";
import AcrInputs from "./AcrInputs";
import FormWrapper from "./FormWrapper";
import MortgageTypeInput from "./MortgageTypeInput";
import RepaymentInputs from "./RepaymentInputs";
import SavingsInputs from "./SavingsInputs";
import InterestRateEntry from "./InterestRateEntry";
import { CurrencyField } from "./CurrencyField";
import {
  SimulationFormProps,
  SimulationFormInputs,
  SimulationFormParams,
} from "../../types/forms";
import SubmitButton from "./SubmitButton";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

const demoParams: SimulationFormInputs = {
  principal: 250000.0,
  interestOnly: false,
  termYears: 25,
  interestRate: undefined,
  desiredMonthlyPayment: undefined,
  additionalCapitalRepayments: false,
  dealLength: 24,
  maxAffordable: 2000.0,
  savingsInterestRate: 3.5,
  saveAtStart: false,
  additionalCapitalRepaymentPercentageLimit: 10,
  reduceTermOnAdditionalCapitalRepayment: false,
  savingsAccounts: [],
};

export default function InterestRateSimulationForm({
  model,
  onSubmit,
  onCancel,
  isEditing,
  editingIndex,
}: SimulationFormProps) {
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const hasInitialized = useRef<boolean>(false);
  const initialSimCountRef = useRef<number>(0);

  // Derive localIndices from model.simulations
  const localIndices = React.useMemo(() => {
    if (!isInitialized) return [];
    if (isEditing) {
      return model.simulations.map((_, i) => i);
    } else {
      // In add mode, only track simulations added by this form
      const startIndex = initialSimCountRef.current;
      return model.simulations.map((_, i) => i).filter((i) => i >= startIndex);
    }
  }, [model.simulations, isInitialized, isEditing]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localIndices.findIndex(
        (idx) => `rate-${idx}` === active.id,
      );
      const newIndex = localIndices.findIndex(
        (idx) => `rate-${idx}` === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        model.moveSimulation(oldIndex, newIndex);
      }
    }
  };

  useEffect(() => {
    if (!isEditing && !hasInitialized.current) {
      hasInitialized.current = true;
      initialSimCountRef.current = model.simulations.length;
      const newSim1: SimulationFormParams = {
        inputs: {
          ...defaultParams,
          interestRate: undefined,
          savingsAccounts: defaultParams.savingsAccounts
            ? [...defaultParams.savingsAccounts]
            : [],
        },
        updated: true,
      };
      const newSim2: SimulationFormParams = {
        inputs: {
          ...defaultParams,
          interestRate: undefined,
          savingsAccounts: defaultParams.savingsAccounts
            ? [...defaultParams.savingsAccounts]
            : [],
        },
        updated: true,
      };
      model.addSimulation(newSim1);
      model.addSimulation(newSim2);
      setIsInitialized(true);
    } else if (isEditing && !hasInitialized.current) {
      initialSimCountRef.current = 0;
      setIsInitialized(true);
      hasInitialized.current = true;
    }
    // This should only run once when the form is mounted for adding new simulations
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    if (!isEditing) {
      // Remove simulations added by this form
      const toRemove = model.simulations
        .map((_, i) => i)
        .filter((i) => i >= initialSimCountRef.current);
      // Remove in reverse order to avoid index shifting issues
      toRemove.reverse().forEach((idx) => {
        model.removeSimulation(idx);
      });
    }
    onCancel();
  };

  const handleAddSimulation = () => {
    const params = model.simulations[localIndices[0]]?.inputs || defaultParams;
    const newSim: SimulationFormParams = {
      inputs: {
        ...params,
        interestRate: undefined,
        savingsAccounts: params.savingsAccounts
          ? params.savingsAccounts.map((a) => ({ ...a }))
          : [],
      },
      updated: true,
    };
    model.addSimulation(newSim);
  };

  const handleRemoveSimulation = (indexInLocalArray: number) => {
    if (localIndices.length <= 2) return; // Keep at least 2 simulations

    const actualIndex = localIndices[indexInLocalArray];
    model.removeSimulation(actualIndex);
  };

  const handleDemo = () => {
    // Clear all existing simulations for this form
    if (!isEditing) {
      const toRemove = model.simulations
        .map((_, i) => i)
        .filter((i) => i >= initialSimCountRef.current);
      toRemove.reverse().forEach((idx) => {
        model.removeSimulation(idx);
      });
    } else {
      // In edit mode, clear all simulations
      const toRemove = model.simulations.map((_, i) => i);
      toRemove.reverse().forEach((idx) => {
        model.removeSimulation(idx);
      });
    }

    // Add two new simulations with demo parameters
    const newSim1: SimulationFormParams = {
      inputs: {
        ...demoParams,
        interestRate: 3.5,
        savingsAccounts: [],
      },
      updated: true,
    };
    const newSim2: SimulationFormParams = {
      inputs: {
        ...demoParams,
        interestRate: 5.0,
        savingsAccounts: [],
      },
      updated: true,
    };
    model.addSimulation(newSim1);
    model.addSimulation(newSim2);

    if (!isEditing) {
      initialSimCountRef.current = model.simulations.length - 2;
    }
  };

  // Wait for initialization before rendering form
  if (!isInitialized) {
    return null;
  }

  if (localIndices == null) {
    return null;
  }

  const firstSimInputs = model.simulations[localIndices[0]]?.inputs || {
    ...defaultParams,
    interestRate: undefined,
  };

  return (
    <FormWrapper onSubmit={onSubmit}>
      {(error) => (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {isEditing ? "Update Simulations" : "Create Simulations"}
            </Typography>
            <Button
              startIcon={<AutoFixHighIcon />}
              onClick={handleDemo}
              variant="outlined"
              size="small"
            >
              Demo
            </Button>
          </Box>

          <CurrencyField
            label="Mortgage Debt"
            value={firstSimInputs.principal}
            onChange={(val) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, { principal: val });
              });
            }}
            fullWidth
            margin="normal"
            required
          />

          <Box sx={{ mt: 2, mb: 2 }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localIndices.map((idx) => `rate-${idx}`)}
                strategy={verticalListSortingStrategy}
              >
                {localIndices.map((actualIndex, i) => {
                  const formInputs = model.simulations[actualIndex]?.inputs || {
                    ...defaultParams,
                    interestRate: undefined,
                  };
                  return (
                    <InterestRateEntry
                      key={actualIndex}
                      id={`rate-${actualIndex}`}
                      index={i}
                      value={formInputs.interestRate}
                      onChange={(val) =>
                        model.updateSimulation(
                          actualIndex,
                          {},
                          { interestRate: val },
                        )
                      }
                      onRemove={() => handleRemoveSimulation(i)}
                      canRemove={localIndices.length > 2}
                      autoFocus={editingIndex === actualIndex}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
            <Button
              startIcon={<Add />}
              onClick={handleAddSimulation}
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
            >
              Add Simulation
            </Button>
          </Box>

          <MortgageTypeInput
            interestOnly={firstSimInputs.interestOnly}
            interestRate={firstSimInputs.interestRate}
            desiredMonthlyPayment={firstSimInputs.desiredMonthlyPayment}
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
          />

          <RepaymentInputs
            interestOnly={firstSimInputs.interestOnly}
            termYears={firstSimInputs.termYears}
            interestRate={firstSimInputs.interestRate}
            desiredMonthlyPayment={firstSimInputs.desiredMonthlyPayment}
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
            mode="interestRate"
          />

          <AcrInputs
            additionalCapitalRepayments={
              firstSimInputs.additionalCapitalRepayments
            }
            dealLength={firstSimInputs.dealLength}
            maxAffordable={firstSimInputs.maxAffordable}
            savingsInterestRate={firstSimInputs.savingsInterestRate}
            saveAtStart={firstSimInputs.saveAtStart}
            additionalCapitalRepaymentPercentageLimit={
              firstSimInputs.additionalCapitalRepaymentPercentageLimit
            }
            reduceTermOnAdditionalCapitalRepayment={
              firstSimInputs.reduceTermOnAdditionalCapitalRepayment
            }
            skipAdditionalCapitalRepayments={
              firstSimInputs.skipAdditionalCapitalRepayments
            }
            interestOnly={firstSimInputs.interestOnly}
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
          />

          <SavingsInputs
            accounts={firstSimInputs.savingsAccounts}
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
          />

          <SubmitButton
            isEditing={isEditing}
            multipleSimulations={true}
            error={error}
            onCancel={handleCancel}
          />
        </>
      )}
    </FormWrapper>
  );
}
